import {
  GridCell,
  GridCellKind,
  type Rectangle,
  SizedGridColumn,
  Theme,
} from "@glideapps/glide-data-grid";
import { isFormulaCell } from "@/payroll/ai-script/cell-kind-utils";
import {
  AI_SPREADSHEET_OVERRIDE_OUTCOME,
  AI_SPREADSHEET_VALUE_TYPE,
  isAiSpreadsheetNumericValueType,
  type AiSpreadsheetValueType,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetCellOverride,
  type IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import { isAiSpreadsheetSheetEditable } from "@/payroll/ai-script/utils";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import { getSafeHyperlinkUrl } from "./hyperlink";
import {
  isBufferedOptimisticManualCell,
  isOptimisticManualCell,
} from "./optimistic-manual-row-edits";
import { pointHeightToPixels } from "./row-resize";
import { volcaColor } from "./spreadsheet-theme";
import { buildSuggestedValueGridCell } from "./suggested-value-cell";
import { getCellErrorCode } from "./utils";

// The single source of truth for whether the sheet allows a cell edit. Pending
// manual-row cells are editable too; the viewer buffers those edits until their
// synthetic ids are reconciled with real server cells.
export const isCellEditable = (cell: IAiSpreadsheetCell): boolean =>
  cell.locked !== true &&
  (!isOptimisticManualCell(cell) || isBufferedOptimisticManualCell(cell));

const hasSuggestedValueOptions = (
  cell: IAiSpreadsheetCell,
): cell is IAiSpreadsheetCell & { suggested_values: string[] } =>
  !isFormulaCell(cell) &&
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  Array.isArray(cell.suggested_values) &&
  cell.suggested_values.length > 0;

type CellContentAlign = "left" | "center" | "right";

const errorThemeOverride = (): Partial<Theme> => ({
  bgCell: volcaColor("--red-5"),
  textDark: volcaColor("--red-70"),
  accentColor: volcaColor("--red-70"),
});

// A cell carrying a persistent manual edit is flagged with a small corner
// triangle (painted by the viewer's drawCell — see cell-markers.ts), not a
// background fill. Blue = applied cleanly; gold = the edit needs review (a
// source input moved or the key was ambiguous this run).
const overrideAppliedMarker = () => volcaColor("--blue-60");
const overrideReviewMarker = () => volcaColor("--gold-50");

// A user-added (manual) row's cells get the same corner marker as an edited
// cell, so an added row reads with the same visual language as an edit.
export const manualRowMarkerColor = (): string => overrideAppliedMarker();

export const overrideMarkerColor = (
  override: IAiSpreadsheetCellOverride,
): string =>
  override.stale ||
  override.outcome === AI_SPREADSHEET_OVERRIDE_OUTCOME.needsReview
    ? overrideReviewMarker()
    : overrideAppliedMarker();

const DEFAULT_COLUMN_WIDTH = 140;
export const DEFAULT_ROW_HEIGHT = 28;
const MIN_COLUMN_COUNT = 8;
const MIN_ROW_COUNT = 20;
// Always render a few empty rows/columns past the content, like a real
// spreadsheet, so the edge of the data never sits flush against the grid edge.
const TRAILING_COLUMN_COUNT = 5;
const TRAILING_ROW_COUNT = 5;
// Stored column widths use Excel "character" units; approximate the pixel
// width with the standard default-font conversion and keep columns legible.
const CHARACTER_WIDTH_PX = 7;
const COLUMN_WIDTH_PADDING_PX = 8;
const MIN_COLUMN_WIDTH_PX = 56;
// Auto-sizing wrapped rows: estimate how many lines the text needs from its
// length and column width, then reserve vertical space for them.
const LINE_HEIGHT_PX = 17;
const ROW_VERTICAL_PADDING_PX = 12;
const APPROX_CHAR_WIDTH_PX = 6.5;
const CELL_HORIZONTAL_PADDING_PX = 16;
const MAX_AUTO_ROW_LINES = 4;

export interface AdaptedSpreadsheetSheet {
  columns: SizedGridColumn[];
  rowCount: number;
  isAddRowCell: (col: number, row: number) => boolean;
  getCellContent: (col: number, row: number) => GridCell;
  // Backs the grid's copy handler (Cmd/Ctrl+C on a selected cell/range). Without
  // it Glide's copy is a no-op — a single-selected cell copies nothing (the user
  // has to enter the edit overlay first). Returns cells row-major for the rect.
  getCellsForSelection: (rect: Rectangle) => GridCell[][];
  getCellRecord: (col: number, row: number) => IAiSpreadsheetCell | undefined;
  // Any manual-row cell in the given (0-based) grid row, or undefined if the row
  // isn't a user-added one. Lets a right-click anywhere on the row — including
  // the row-number marker (which has no column cell) — offer "Delete row".
  getManualRowCell: (row: number) => IAiSpreadsheetCell | undefined;
  getSearchMatches: (query: string) => SpreadsheetSearchMatch[];
  getTextOverflow: (col: number, row: number) => TextOverflowSpan | undefined;
  getRowHeight: (row: number) => number;
}

interface SpreadsheetAdapterOptions {
  onHyperlinkClick?: (url: string) => void;
}

export interface SpreadsheetSearchMatch {
  cellId: string;
  col: number;
  row: number;
}

export interface TextOverflowSpan {
  sourceCol: number;
  displayData: string;
  themeOverride: Partial<Theme> | undefined;
}

const coordinateKey = (col: number, row: number) => `${row}:${col}`;

// Excel stores colors as RGB ("EB0044") or ARGB ("FF000000"); normalize both
// to CSS so Glide can paint the original cell styling.
const normalizeColor = (value?: string | null): string | undefined => {
  if (!value) return undefined;

  const hex = value.replace(/^#/, "");

  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex}`;

  if (/^[0-9a-fA-F]{8}$/.test(hex)) {
    const alpha = parseInt(hex.slice(0, 2), 16) / 255;
    const r = parseInt(hex.slice(2, 4), 16);
    const g = parseInt(hex.slice(4, 6), 16);
    const b = parseInt(hex.slice(6, 8), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return undefined;
};

// The format the toolbar/menu shows for a cell, matching Excel's ribbon: it
// reflects the cell's persistent number format (its format_code), NOT the
// content's value_type. So a currency-formatted cell reads "Currency" even
// while it holds text, and a cell with no format code reads "Text" (the
// default) even if its value_type is numeric. The number/text split for a cell
// without a currency/percent/date code falls back to value_type so an explicit
// "Number" format round-trips (number carries no distinct code).
export const getCellFormatType = (
  cell: IAiSpreadsheetCell,
): AiSpreadsheetValueType => {
  const code = cell.format?.format_code ?? "";

  if (code.includes("$")) return AI_SPREADSHEET_VALUE_TYPE.currency;
  if (code.includes("%")) return AI_SPREADSHEET_VALUE_TYPE.percent;
  if (/[dmy]/i.test(code)) return AI_SPREADSHEET_VALUE_TYPE.date;

  return isAiSpreadsheetNumericValueType(cell.value_type)
    ? AI_SPREADSHEET_VALUE_TYPE.number
    : AI_SPREADSHEET_VALUE_TYPE.text;
};

const hasVisibleValue = (cell: IAiSpreadsheetCell | undefined) =>
  cell ? getAiSpreadsheetCellDisplayValue(cell) !== "" : false;

// The grid's ordinary clipboard fallback should match text/plain and expose the
// computed display value. Formula source is carried by the viewer's private
// clipboard metadata when that richer copy path is available.
const getCopyValue = (cell: IAiSpreadsheetCell) =>
  getAiSpreadsheetCellDisplayValue(cell);

export const getInCellEditValue = (cell: IAiSpreadsheetCell) => {
  if (isFormulaCell(cell)) {
    return cell.raw_input ?? "";
  }

  return getAiSpreadsheetCellDisplayValue(cell);
};

const getContentAlign = (
  cell: IAiSpreadsheetCell,
): CellContentAlign | undefined => {
  const horizontal = cell.format?.alignment?.horizontal;

  return horizontal === "left" ||
    horizontal === "center" ||
    horizontal === "right"
    ? horizontal
    : undefined;
};

const buildThemeOverride = (
  cell: IAiSpreadsheetCell,
): Partial<Theme> | undefined => {
  const override: Partial<Theme> = {};
  // Match Spreadsheet::FormatContract: choose the first nonblank alias before
  // converting the stored color into the grid's CSS format.
  const bgColor = normalizeColor(
    [
      cell.format?.background_color,
      cell.format?.bg_color,
      cell.format?.fill_color,
    ].find((value) => value?.trim()),
  );
  const fgColor = normalizeColor(
    [cell.format?.font_color, cell.format?.fg_color].find((value) =>
      value?.trim(),
    ),
  );
  const errorCode = getCellErrorCode(cell);

  if (bgColor) override.bgCell = bgColor;
  if (fgColor) override.textDark = fgColor;
  if (cell.format?.bold) override.baseFontStyle = "600 13px";

  // A user-added row is flagged with the same corner marker as an edit (drawn in
  // the viewer's drawCell), not a background fill.

  // Error styling takes precedence over the cell's stored formatting. A
  // persistent edit is signalled by a corner marker (drawCell), not the
  // background, so it doesn't participate here.
  const merged = errorCode
    ? { ...override, ...errorThemeOverride() }
    : override;

  return Object.keys(merged).length > 0 ? merged : undefined;
};

// The blank row directly under the data reads as an editable "type here to add
// a row" cell, faintly tinted like a manual row so it's discoverable.
const newRowThemeOverride = (): Partial<Theme> => ({
  bgCell: volcaColor("--blue-0"),
});

const buildNewRowCell = (): GridCell => ({
  kind: GridCellKind.Text,
  allowOverlay: true,
  readonly: false,
  displayData: "",
  data: "",
  themeOverride: newRowThemeOverride(),
});

const buildGridCell = (
  cell: IAiSpreadsheetCell | undefined,
  editable: boolean,
  onHyperlinkClick?: (url: string) => void,
): GridCell => {
  if (!cell) {
    return {
      kind: GridCellKind.Text,
      allowOverlay: false,
      readonly: true,
      displayData: "",
      data: "",
    };
  }

  const displayData = getAiSpreadsheetCellDisplayValue(cell);
  const locked = !isCellEditable(cell);
  const hyperlinkUrl = getCellErrorCode(cell)
    ? null
    : getSafeHyperlinkUrl(cell.raw_input);

  if (editable && !locked && hasSuggestedValueOptions(cell)) {
    return buildSuggestedValueGridCell({
      value: getInCellEditValue(cell),
      suggestions: cell.suggested_values,
      contentAlign: getContentAlign(cell),
      themeOverride: buildThemeOverride(cell),
    });
  }

  if (hyperlinkUrl) {
    return {
      kind: GridCellKind.Uri,
      allowOverlay: editable && !locked,
      readonly: !editable || locked,
      displayData,
      data: getInCellEditValue(cell),
      copyData: getCopyValue(cell),
      contentAlign: getContentAlign(cell),
      hoverEffect: true,
      themeOverride: buildThemeOverride(cell),
      onClickUri: onHyperlinkClick
        ? () => onHyperlinkClick(hyperlinkUrl)
        : undefined,
    };
  }

  return {
    kind: GridCellKind.Text,
    allowOverlay: editable && !locked,
    readonly: !editable || locked,
    displayData,
    data: getInCellEditValue(cell),
    copyData: getCopyValue(cell),
    contentAlign: getContentAlign(cell),
    allowWrapping: cell.format?.alignment?.wrap_text ?? false,
    themeOverride: buildThemeOverride(cell),
  };
};

// Cell coordinates and dimension maps from the API are 1-based, while Glide
// addresses everything 0-based.
const getDimensionValue = (
  dimensions: Record<string, number> | number[] | null,
  oneBasedIndex: number,
  fallback: number,
) => {
  const value = Array.isArray(dimensions)
    ? dimensions[oneBasedIndex - 1]
    : dimensions?.[String(oneBasedIndex)];

  return value ?? fallback;
};

const getColumnWidth = (
  columnWidths: Record<string, number> | number[] | null,
  oneBasedIndex: number,
) => {
  const characters = getDimensionValue(columnWidths, oneBasedIndex, NaN);

  if (Number.isNaN(characters)) return DEFAULT_COLUMN_WIDTH;

  return Math.max(
    MIN_COLUMN_WIDTH_PX,
    Math.round(characters * CHARACTER_WIDTH_PX) + COLUMN_WIDTH_PADDING_PX,
  );
};

// Inverse of getColumnWidth: convert a pixel width from a drag-resize back into
// the Excel "character" units the API stores, so widths round-trip with export.
export const pixelWidthToCharacterUnits = (pixels: number) =>
  Math.max((pixels - COLUMN_WIDTH_PADDING_PX) / CHARACTER_WIDTH_PX, 0);

const getColumnTitle = (index: number) => {
  let columnNumber = index + 1;
  let title = "";

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    title = String.fromCharCode(65 + remainder) + title;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return title;
};

const getStoredRowHeight = (
  rowHeights: Record<string, number> | number[] | null,
  oneBasedIndex: number,
): number | undefined => {
  const points = Array.isArray(rowHeights)
    ? rowHeights[oneBasedIndex - 1]
    : rowHeights?.[String(oneBasedIndex)];

  if (points === undefined || points === null) return undefined;

  return pointHeightToPixels(points);
};

// Estimate the line count a wrapped cell needs from its text length and the
// available column width, since Glide does not auto-grow rows to fit content.
const estimateWrappedLines = (text: string, columnWidth: number): number => {
  const usableWidth = Math.max(columnWidth - CELL_HORIZONTAL_PADDING_PX, 1);
  const lines = Math.ceil((text.length * APPROX_CHAR_WIDTH_PX) / usableWidth);

  return Math.min(Math.max(lines, 1), MAX_AUTO_ROW_LINES);
};

const buildRowHeights = (
  sheet: IAiSpreadsheetLoadedSheet,
  columns: SizedGridColumn[],
  rowCount: number,
  cellsByCoordinate: Map<string, IAiSpreadsheetCell>,
): Map<number, number> => {
  const heights = new Map<number, number>();

  for (let row = 1; row <= rowCount; row++) {
    const storedHeight = getStoredRowHeight(sheet.row_heights, row);
    if (storedHeight !== undefined) {
      heights.set(row - 1, storedHeight);
      continue;
    }
    let lines = 1;

    columns.forEach((column, columnIndex) => {
      const cell = cellsByCoordinate.get(coordinateKey(columnIndex + 1, row));

      if (cell?.format?.alignment?.wrap_text) {
        lines = Math.max(
          lines,
          estimateWrappedLines(
            getAiSpreadsheetCellDisplayValue(cell),
            column.width,
          ),
        );
      }
    });

    const wrappedHeight =
      lines > 1 ? lines * LINE_HEIGHT_PX + ROW_VERTICAL_PADDING_PX : 0;

    heights.set(row - 1, Math.max(DEFAULT_ROW_HEIGHT, wrappedHeight));
  }

  return heights;
};

const canTextOverflow = (cell: IAiSpreadsheetCell) => {
  const displayData = getAiSpreadsheetCellDisplayValue(cell);
  const align = getContentAlign(cell);

  return (
    !getCellErrorCode(cell) &&
    !hasSuggestedValueOptions(cell) &&
    displayData !== "" &&
    !displayData.includes("\n") &&
    !cell.format?.alignment?.wrap_text &&
    (align === undefined || align === "left")
  );
};

const buildTextOverflowSpans = (
  columnCount: number,
  cellsByCoordinate: Map<string, IAiSpreadsheetCell>,
): Map<string, TextOverflowSpan> => {
  const spans = new Map<string, TextOverflowSpan>();

  cellsByCoordinate.forEach((cell) => {
    if (!canTextOverflow(cell)) return;

    const sourceCol = cell.col - 1;
    let endColExclusive = sourceCol + 1;

    for (let col = sourceCol + 1; col < columnCount; col++) {
      const nextCell = cellsByCoordinate.get(coordinateKey(col + 1, cell.row));

      if (hasVisibleValue(nextCell)) break;

      endColExclusive = col + 1;
    }

    if (endColExclusive === sourceCol + 1) return;

    const span: TextOverflowSpan = {
      sourceCol,
      displayData: getAiSpreadsheetCellDisplayValue(cell),
      themeOverride: buildThemeOverride(cell),
    };

    for (let col = sourceCol; col < endColExclusive; col++) {
      spans.set(coordinateKey(col + 1, cell.row), span);
    }
  });

  return spans;
};

export const adaptSpreadsheetSheet = (
  sheet: IAiSpreadsheetLoadedSheet,
  { onHyperlinkClick }: SpreadsheetAdapterOptions = {},
): AdaptedSpreadsheetSheet => {
  const cellsByCoordinate = new Map<string, IAiSpreadsheetCell>();
  let maxCol = 0;
  let maxRow = 0;

  sheet.cells.forEach((cell) => {
    cellsByCoordinate.set(coordinateKey(cell.col, cell.row), cell);
    maxCol = Math.max(maxCol, cell.col);
    maxRow = Math.max(maxRow, cell.row);
  });

  // API coordinates are 1-based, so the last populated cell maps to the
  // 0-based Glide index `max - 1`; column/row counts therefore equal `max`,
  // plus a few trailing empties so there's always room past the content.
  const columnCount = Math.max(
    maxCol + TRAILING_COLUMN_COUNT,
    MIN_COLUMN_COUNT,
  );
  const rowCount = Math.max(maxRow + TRAILING_ROW_COUNT, MIN_ROW_COUNT);

  const columns: SizedGridColumn[] = Array.from(
    { length: columnCount },
    (_, index) => ({
      id: String(index),
      title: getColumnTitle(index),
      width: getColumnWidth(sheet.column_widths, index + 1),
    }),
  );

  const rowHeights = buildRowHeights(
    sheet,
    columns,
    rowCount,
    cellsByCoordinate,
  );
  const textOverflowSpans = buildTextOverflowSpans(
    columnCount,
    cellsByCoordinate,
  );
  const searchableCells = sheet.cells
    .map((cell) => ({
      cell,
      displayValue: getAiSpreadsheetCellDisplayValue(cell).toLocaleLowerCase(),
    }))
    .filter(({ displayValue }) => displayValue !== "")
    .sort((a, b) => a.cell.row - b.cell.row || a.cell.col - b.cell.col);

  const getRecord = (col: number, row: number) =>
    cellsByCoordinate.get(coordinateKey(col + 1, row + 1));
  const getManualRowCell = (row: number) =>
    Array.from(cellsByCoordinate.values()).find(
      (cell) => cell.row === row + 1 && !!cell.manual_row_id,
    );
  const getTextOverflow = (col: number, row: number) =>
    textOverflowSpans.get(coordinateKey(col + 1, row + 1));
  const editable = isAiSpreadsheetSheetEditable(sheet);
  const isAddRowCell = (col: number, row: number) =>
    editable && row === maxRow && col < maxCol;

  const getCellContent = (col: number, row: number): GridCell => {
    const record = getRecord(col, row);
    if (!record && isAddRowCell(col, row)) {
      return buildNewRowCell();
    }
    return buildGridCell(record, editable, onHyperlinkClick);
  };

  return {
    columns,
    rowCount,
    isAddRowCell,
    getCellContent,
    getCellsForSelection: ({ x, y, width, height }) => {
      const rows: GridCell[][] = [];
      for (let row = y; row < y + height; row++) {
        const cols: GridCell[] = [];
        for (let col = x; col < x + width; col++) {
          cols.push(getCellContent(col, row));
        }
        rows.push(cols);
      }
      return rows;
    },
    getCellRecord: (col, row) => getRecord(col, row),
    getManualRowCell,
    getSearchMatches: (query) => {
      const normalizedQuery = query.trim().toLocaleLowerCase();

      if (!normalizedQuery) return [];

      return searchableCells
        .filter(({ displayValue }) => displayValue.includes(normalizedQuery))
        .map(({ cell }) => ({
          cellId: cell.id,
          col: cell.col - 1,
          row: cell.row - 1,
        }));
    },
    getTextOverflow,
    getRowHeight: (row) => rowHeights.get(row) ?? DEFAULT_ROW_HEIGHT,
  };
};
